import React, { useState, useEffect, useRef } from 'react';
import { AuthProvider, useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, CheckCircle, Lock, Mail, AlertCircle } from 'lucide-react';

// Verification Boxes Component with Animation
const VerificationBoxes = ({ onComplete }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);
  const [digitsLeft, setDigitsLeft] = useState(6);

  useEffect(() => {
    // Calculate digits left
    const filledDigits = code.filter(digit => digit !== '').length;
    setDigitsLeft(6 - filledDigits);
    
    // Call onComplete when all digits are filled
    if (filledDigits === 6) {
      onComplete(code);
    }
  }, [code, onComplete]);

  // Handle input change
  const handleChange = (index, value) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;
    
    // Create a new array with the updated value
    const newCode = [...code];
    newCode[index] = value.slice(0, 1); // Only take first character
    setCode(newCode);
    
    // Auto-focus next input if a digit was entered
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  // Handle keydown
  const handleKeyDown = (index, e) => {
    // Move to previous input when backspace is pressed on an empty input
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
    
    // Handle arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1].focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();
    
    // Check if pasted content is all digits
    if (/^\d+$/.test(pastedData)) {
      const digits = pastedData.split('').slice(0, 6);
      const newCode = [...code];
      
      digits.forEach((digit, index) => {
        if (index < 6) newCode[index] = digit;
      });
      
      setCode(newCode);
      
      // Focus the appropriate input after paste
      if (digits.length < 6) {
        inputRefs.current[digits.length].focus();
      } else {
        inputRefs.current[5].focus();
      }
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full"
    >
      <div className="flex space-x-2 md:space-x-4 justify-center mb-6">
        {code.map((digit, index) => (
          <motion.div
            key={index}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: index * 0.08 }}
            className={`w-12 h-16 rounded-lg flex items-center justify-center ${digit ? 'border-2 border-green shadow-md' : 'border-2 border-gray-200'}`}
          >
            <input
              ref={el => inputRefs.current[index] = el}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              className="w-full h-full text-center text-2xl bg-transparent border-none outline-none"
            />
          </motion.div>
        ))}
      </div>
      
      <motion.div 
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="bg-beige-light p-3 rounded-lg text-center mt-4"
      >
        <p className="text-gray-600 text-lg">{digitsLeft} digits left</p>
      </motion.div>
    </motion.div>
  );
};

// Message Component with Animation
const Message = ({ message, type = 'info' }) => {
  const bgColor = type === 'error' ? 'bg-red-50' : 
                 type === 'success' ? 'bg-green-50' : 'bg-blue-50';
  const textColor = type === 'error' ? 'text-red-700' : 
                   type === 'success' ? 'text-green-700' : 'text-blue-700';
  const Icon = type === 'error' ? AlertCircle : 
              type === 'success' ? CheckCircle : Mail;

  return message ? (
    <motion.div 
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className={`p-4 mb-4 rounded-lg flex items-start ${bgColor}`}
    >
      <Icon className={`mr-2 ${textColor}`} size={20} />
      <p className={textColor}>{message}</p>
    </motion.div>
  ) : null;
};

// PurchaseFormContent with animations
const PurchaseFormContent = ({ productId }) => {
  const { user, signUp, verifyOtp, signIn } = useAuth();
  
  // Initialize state from localStorage if available
  const [email, setEmail] = useState(() => {
    const savedEmail = localStorage.getItem('purchase_email');
    return savedEmail || '';
  });
  
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [otpCode, setOtpCode] = useState('');
  
  // Initialize form visibility states from localStorage if available
  const [showVerificationForm, setShowVerificationForm] = useState(() => {
    return localStorage.getItem('purchase_show_verification_form') === 'true';
  });
  
  const [showOtpForm, setShowOtpForm] = useState(() => {
    return localStorage.getItem('purchase_show_otp_form') === 'true';
  });
  
  const [showLoginForm, setShowLoginForm] = useState(() => {
    return localStorage.getItem('purchase_show_login_form') === 'true';
  });
  
  const [showPasswordSetupForm, setShowPasswordSetupForm] = useState(() => {
    return localStorage.getItem('purchase_show_password_setup_form') === 'true';
  });
  
  const [requiresPasswordSetup, setRequiresPasswordSetup] = useState(() => {
    return localStorage.getItem('purchase_requires_password_setup') === 'true';
  });
  
  const [loading, setLoading] = useState(false);
  const [purchaseComplete, setPurchaseComplete] = useState(() => {
    return localStorage.getItem('purchase_complete') === 'true';
  });
  const [debug, setDebug] = useState({});

  // Save state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('purchase_email', email);
  }, [email]);

  useEffect(() => {
    localStorage.setItem('purchase_show_verification_form', showVerificationForm);
  }, [showVerificationForm]);

  useEffect(() => {
    localStorage.setItem('purchase_show_otp_form', showOtpForm);
  }, [showOtpForm]);

  useEffect(() => {
    localStorage.setItem('purchase_show_login_form', showLoginForm);
  }, [showLoginForm]);

  useEffect(() => {
    localStorage.setItem('purchase_show_password_setup_form', showPasswordSetupForm);
  }, [showPasswordSetupForm]);

  useEffect(() => {
    localStorage.setItem('purchase_requires_password_setup', requiresPasswordSetup);
  }, [requiresPasswordSetup]);

  useEffect(() => {
    localStorage.setItem('purchase_complete', purchaseComplete);
  }, [purchaseComplete]);
  
  // Check if user is already logged in on component mount
  useEffect(() => {
    if (user) {
      // If user is logged in but we're still showing verification forms,
      // update state to reflect the logged in status
      if (showVerificationForm || showOtpForm || showLoginForm || showPasswordSetupForm) {
        setShowVerificationForm(false);
        setShowOtpForm(false);
        setShowLoginForm(false);
        setShowPasswordSetupForm(false);
        setMessage('Che piacere rivederti! Hai gia effettuato laccesso');
        setMessageType('success');
      }
    }
  }, [user]);

  // Clear purchase state
  const clearPurchaseState = () => {
    localStorage.removeItem('purchase_email');
    localStorage.removeItem('purchase_show_verification_form');
    localStorage.removeItem('purchase_show_otp_form');
    localStorage.removeItem('purchase_show_login_form');
    localStorage.removeItem('purchase_show_password_setup_form');
    localStorage.removeItem('purchase_requires_password_setup');
    localStorage.removeItem('purchase_complete');
    
    setEmail('');
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setMessage('');
    setOtpCode('');
    setShowVerificationForm(false);
    setShowOtpForm(false);
    setShowLoginForm(false);
    setShowPasswordSetupForm(false);
    setRequiresPasswordSetup(false);
    setPurchaseComplete(false);
  };

  // Function to ensure a user profile exists and has_password is set correctly
  const ensureUserProfile = async (userId, userEmail) => {
    try {
      // First, check if a profile already exists by ID
      const { data: profileData, error: fetchError } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();
        
      if (fetchError || !profileData) {
        console.log("No profile found by ID, checking by email:", userEmail);
        
        // Try to find profile by email (case-insensitive)
        const { data: emailProfileData, error: emailFetchError } = await supabase
          .from('users')
          .select('*')
          .ilike('email', userEmail)
          .single();
          
        if (emailFetchError || !emailProfileData) {
          console.log("No profile found by email either, creating new profile:", userId, userEmail);
          
          // Create a new profile if not found by ID or email
          const { data: newProfile, error: createError } = await supabase
            .from('users')
            .insert([{ 
              id: userId, 
              email: userEmail,
              has_password: true
            }])
            .select()
            .single();
            
          if (createError) {
            console.error("Error creating profile:", createError);
          } else {
            console.log("Profile created successfully:", newProfile);
          }
        } else {
          // Profile found by email but ID doesn't match
          console.log("Profile found by email but ID doesn't match. Updating...");
          
          // Update email profile to add correct ID and has_password
          const { error: updateError } = await supabase
            .from('users')
            .update({ 
              id: userId,
              email: userEmail, // Ensure consistent casing with auth
              has_password: true 
            })
            .eq('id', emailProfileData.id);
            
          if (updateError) {
            console.error("Error updating profile with ID:", updateError);
          } else {
            console.log("Profile updated with correct ID");
          }
        }
      } else {
        console.log("Profile already exists:", profileData);
        
        // Make sure has_password is set correctly and email has consistent casing
        if (!profileData.has_password || profileData.email !== userEmail) {
          const { error: updateError } = await supabase
            .from('users')
            .update({ 
              has_password: true,
              email: userEmail // Ensure consistent casing with auth
            })
            .eq('id', userId);
            
          if (updateError) {
            console.error("Error updating profile:", updateError);
          } else {
            console.log("Updated profile with correct email and has_password");
          }
        }
      }
    } catch (error) {
      console.error("Error in ensureUserProfile:", error);
    }
  };

  // Process the purchase via API
// In the processPurchase function, modify it to handle the "already_purchased" status
const processPurchase = async (userEmail) => {
  setLoading(true);
  try {
    // Get the current website URL
    const websiteRootUrl = window.location.origin;
    
    // Create the success and cancel URLs
    const successUrl = `${websiteRootUrl}/contenuti`;
    const cancelUrl = window.location.href; // Current page URL for cancel
    
    const response = await fetch('http://localhost:8888/api/simple-purchase-intent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: userEmail,
        productId: productId,
        success_url: successUrl,
        cancel_url: cancelUrl
      }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Error processing purchase');
    }
    
    // Check if the product was already purchased
    if (data.purchase_status === "already_purchased") {
      setMessage(data.message || 'Hai già acquistato questo prodotto!');
      setMessageType('info');
      
      // Redirect to contenuti page after a short delay
      setTimeout(() => {
        window.location.href = '/contenuti';
      }, 1500);
      
      return true;
    }
    
    // If checkout URL is provided in response, redirect to it
    if (data.checkout_url) {
      window.location.href = data.checkout_url;
      return true;
    }
    
    setMessage('Purchase successful! Thank you for your order.');
    setMessageType('success');
    setPurchaseComplete(true);
    // Clear stored state on successful purchase
    clearPurchaseState();
    return true;
  } catch (error) {
    console.error('Purchase error:', error);
    setMessage(`Purchase failed: ${error.message}`);
    setMessageType('error');
    return false;
  } finally {
    setLoading(false);
  }
};

  

  // Handle purchase button click
  const handlePurchase = () => {
    if (user) {
      // User is logged in, proceed with purchase directly
      processPurchase(user.email);
    } else {
      // User is not logged in, show email form
      setShowVerificationForm(true);
    }
  };

  // Handle "Forgot Password" flow
  const handleForgotPassword = async () => {
    if (!email) {
      setMessage('Please enter your email address');
      setMessageType('error');
      return;
    }
    
    setLoading(true);
    
    try {
      // Send a password reset email
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.href,
      });
      
      if (error) {
        setMessage(`Error: ${error.message}`);
        setMessageType('error');
      } else {
        setMessage('Please check your email for a verification code.');
        setMessageType('info');
        setShowLoginForm(false);
        setShowOtpForm(true);
        setRequiresPasswordSetup(true); // They'll need to set a new password
      }
    } catch (error) {
      console.error('Error in handleForgotPassword:', error);
      setMessage(`An error occurred: ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  // Handle email verification with debugging
  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setMessage('Please enter a valid email address');
      setMessageType('error');
      return;
    }
  
    setLoading(true);
    const debugData = {}; // Collect debug info
    
    try {
      // STEP 1: Check if the user exists in users with case-insensitive email match
      const { data: profileData, error: profileError } = await supabase
        .from('users')
        .select('id, email, has_password')
        .ilike('email', email) // ilike for case-insensitive matching
        .maybeSingle();
        
      debugData.profileCheck = { data: profileData, error: profileError };
      
      // If profile exists and has_password is true, show login form
      if (profileData && profileData.has_password === true) {
        setMessage('Please enter your password to continue.');
        setMessageType('info');
        setShowLoginForm(true);
        setShowOtpForm(false);
        setLoading(false);
        setDebug(debugData);
        return;
      }
      
      // STEP 2: Try to sign up
      const { data: signUpData, error: signUpError } = await signUp(email, Math.random().toString(36).slice(-8), {});
      
      debugData.signUpAttempt = { data: signUpData, error: signUpError };
      
      if (!signUpError) {
        // New user successfully created
        setMessage('Please check your email for verification code');
        setMessageType('info');
        setShowOtpForm(true);
        setRequiresPasswordSetup(true);
        setLoading(false);
        setDebug(debugData);
        return;
      } 
      
      const userExists = signUpError && signUpError.message && 
                        (signUpError.message.includes('already registered') || 
                         signUpError.message.includes('already exists'));
      
      if (userExists) {
        // Try to sign in with empty password to check if password is set
        const { error: passwordCheckError } = await supabase.auth.signInWithPassword({
          email,
          password: 'invalid_password_check'
        });
        
        debugData.passwordCheck = { error: passwordCheckError };
        
        const hasInvalidCredentialsError = passwordCheckError && 
                                          passwordCheckError.message && 
                                          passwordCheckError.message.includes('Invalid login credentials');
        
        if (hasInvalidCredentialsError) {
          // If we get "Invalid login credentials", it means the user has a password set
          // Update the profile to make sure has_password is set to true
          if (profileData) {
            await supabase
              .from('users')
              .update({ has_password: true })
              .eq('id', profileData.id);
          }
          
          setMessage('Please enter your password to continue.');
          setMessageType('info');
          setShowLoginForm(true);
          setShowOtpForm(false);
          setLoading(false);
          setDebug(debugData);
          return;
        }
        
        // If we reach here, the user exists but might not have a password
        // Or we couldn't determine password status, so send an OTP for safety
        const { error: otpError } = await supabase.auth.signInWithOtp({
          email: email,
          options: {
            shouldCreateUser: false
          }
        });
        
        debugData.otpAttempt = { error: otpError };
        
        if (otpError) {
          setMessage(`Error: ${otpError.message}`);
          setMessageType('error');
        } else {
          // If profile exists but has_password is false or null, they'll need to set a password
          setRequiresPasswordSetup(!profileData || !profileData.has_password);
          setMessage('Please check your email for verification code');
          setMessageType('info');
          setShowOtpForm(true);
        }
      } else {
        // Some other signup error occurred
        setMessage(`Error: ${signUpError.message}`);
        setMessageType('error');
      }
      
    } catch (error) {
      console.error('Error in handleVerifyEmail:', error);
      setMessage(`An error occurred: ${error.message}`);
      setMessageType('error');
      debugData.finalError = error;
    } finally {
      setLoading(false);
      setDebug(debugData);
    }
  };
  
  // Add a reset button to allow users to start over
  const renderResetButton = () => {
    if (showVerificationForm || showOtpForm || showLoginForm || showPasswordSetupForm) {
      return (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          type="button"
          onClick={clearPurchaseState}
          className="w-full text-gray-500 text-sm mt-6 hover:text-gray-700 transition-colors"
        >
          Start Over
        </motion.button>
      );
    }
    return null;
  };

  // Button animation variants
  const buttonVariants = {
    initial: { scale: 0.95, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    tap: { scale: 0.98 },
    hover: { scale: 1.02 }
  };

  // Form animation variants
  const formVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.2 } }
  };

  // Render appropriate form based on state
  const renderForm = () => {
    if (purchaseComplete) {
      return (
        <motion.div
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={formVariants}
          className="p-6 text-center bg-green-50 rounded-lg shadow-md"
        >
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="flex justify-center mb-4"
          >
            <CheckCircle size={60} className="text-green-500" />
          </motion.div>
          <h3 className="text-xl font-bold text-green-700 mb-2">Success!</h3>
          <p className="text-green-600">Your purchase has been completed.</p>
        </motion.div>
      );
    }
    
    if (!showVerificationForm) {
      return (
        <motion.button
          variants={buttonVariants}
          initial="initial"
          animate="animate"
          whileTap="tap"
          whileHover="hover"
          onClick={handlePurchase}
          disabled={loading}
          className="w-full py-4 px-6 bg-green text-white rounded-full font-bold text-lg shadow-md hover:bg-accent transition-colors duration-300"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
              Processing...
            </div>
          ) : 'Acquista'}
        </motion.button>
      );
    }
    
    if (showPasswordSetupForm) {
      return (
        <motion.form 
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={formVariants}
          onSubmit={async (e) => {
            e.preventDefault();
            
            if (newPassword !== confirmPassword) {
              setMessage('Passwords do not match');
              setMessageType('error');
              return;
            }
            
            if (newPassword.length < 8) {
              setMessage('Password must be at least 8 characters long');
              setMessageType('error');
              return;
            }
            
            setLoading(true);
            
            try {
              // Update user with new password
              const { data, error: updateError } = await supabase.auth.updateUser({
                password: newPassword
              });
              
              if (updateError) {
                throw updateError;
              }
              
              // Ensure the profile exists and has has_password set to true
              if (data && data.user) {
                await ensureUserProfile(data.user.id, data.user.email);
              }
              
              setMessage('Password set successfully!');
              setMessageType('success');
              // Process the purchase after password setup
              await processPurchase(email);
            } catch (error) {
              setMessage(`Error setting password: ${error.message}`);
              setMessageType('error');
            } finally {
              setLoading(false);
            }
          }} 
          className="space-y-4"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h2 className="text-2xl font-bold text-green mb-6 text-center">Create Your Password</h2>
            <p className="text-gray-600 mb-6 text-center">Set a secure password to complete your account setup</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-4"
          >
            <label 
              htmlFor="newPassword" 
              className="block mb-2 font-medium text-gray-700"
            >
              New Password:
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 text-gray-400" size={18} />
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className="w-full p-3 pl-10 border-2 border-gray-200 rounded-lg focus:border-green focus:ring-0 transition-colors outline-none"
                placeholder="Minimum 8 characters"
              />
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-6"
          >
            <label 
              htmlFor="confirmPassword" 
              className="block mb-2 font-medium text-gray-700"
            >
              Confirm Password:
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 text-gray-400" size={18} />
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full p-3 pl-10 border-2 border-gray-200 rounded-lg focus:border-green focus:ring-0 transition-colors outline-none"
                placeholder="Confirm your password"
              />
            </div>
          </motion.div>
          
          <motion.button
            variants={buttonVariants}
            initial="initial"
            animate="animate"
            whileTap="tap"
            whileHover="hover"
            disabled={loading || !newPassword || !confirmPassword}
            type="submit"
            className="w-full py-3 px-6 bg-green text-white rounded-full font-bold shadow-md hover:bg-accent transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
                Setting Password...
              </div>
            ) : 'Set Password & Continue'}
          </motion.button>
          
          {renderResetButton()}
        </motion.form>
      );
    }
    
    if (showLoginForm) {
      return (
        <motion.form 
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={formVariants}
          onSubmit={async (e) => {
            e.preventDefault();
            setLoading(true);
            
            try {
              // Attempt to sign in with email and password
              const { data, error } = await signIn({ email, password });
              
              if (error) {
                setMessage(`Login failed: ${error.message}`);
                setMessageType('error');
              } else {
                setMessage('Login successful!');
                setMessageType('success');
                
                // Ensure profile exists and has has_password set to true
                if (data && data.user) {
                  await ensureUserProfile(data.user.id, data.user.email);
                }
                
                // Process purchase after successful login
                await processPurchase(email);
              }
            } catch (error) {
              setMessage(`Error during login: ${error.message}`);
              setMessageType('error');
            } finally {
              setLoading(false);
            }
          }} 
          className="space-y-4"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h2 className="text-2xl font-bold text-green mb-6 text-center">Welcome Back</h2>
            <p className="text-gray-600 mb-6 text-center">Log in to your account to complete your purchase</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-4"
          >
            <label 
              htmlFor="email" 
              className="block mb-2 font-medium text-gray-700"
            >
              Email:
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 text-gray-400" size={18} />
              <input
id="email"
type="email"
value={email}
readOnly
className="w-full p-3 pl-10 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-600"
/>
</div>
</motion.div>

<motion.div
initial={{ opacity: 0, x: -10 }}
animate={{ opacity: 1, x: 0 }}
transition={{ delay: 0.3 }}
className="mb-6"
>
<label 
htmlFor="password" 
className="block mb-2 font-medium text-gray-700"
>
Password:
</label>
<div className="relative">
<Lock className="absolute left-3 top-3 text-gray-400" size={18} />
<input
id="password"
type="password"
value={password}
onChange={(e) => setPassword(e.target.value)}
required
className="w-full p-3 pl-10 border-2 border-gray-200 rounded-lg focus:border-green focus:ring-0 transition-colors outline-none"
placeholder="Enter your password"
/>
</div>
</motion.div>

<motion.button
variants={buttonVariants}
initial="initial"
animate="animate"
whileTap="tap"
whileHover="hover"
disabled={loading || !password}
type="submit"
className="w-full py-3 px-6 bg-green text-white rounded-full font-bold shadow-md hover:bg-accent transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
>
{loading ? (
<div className="flex items-center justify-center">
<div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
Logging in...
</div>
) : 'Log In'}
</motion.button>

<motion.button
initial={{ opacity: 0, y: 10 }}
animate={{ opacity: 1, y: 0 }}
transition={{ delay: 0.4 }}
type="button"
onClick={handleForgotPassword}
disabled={loading}
className="w-full mt-4 py-3 px-6 bg-transparent text-green border-2 border-green rounded-full font-medium hover:bg-green-50 transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
>
{loading ? 'Processing...' : 'Forgot Password?'}
</motion.button>

<motion.button
initial={{ opacity: 0 }}
animate={{ opacity: 1 }}
transition={{ delay: 0.5 }}
type="button"
onClick={() => {
setShowLoginForm(false);
setShowVerificationForm(false);
}}
className="w-full text-green text-sm mt-4 hover:text-accent transition-colors"
>
Back to purchase
</motion.button>

{renderResetButton()}
</motion.form>
);
}

if (showOtpForm) {
return (
<motion.form 
initial="hidden"
animate="visible"
exit="exit"
variants={formVariants}
onSubmit={async (e) => {
e.preventDefault();
setLoading(true);

try {
// Call verifyOtp function from AuthContext
const { data, error } = await verifyOtp(email, otpCode);

if (error) {
setMessage(`Verification failed: ${error.message}`);
setMessageType('error');
} else {
// After successful verification
if (data && data.user) {
  // Always ensure a profile exists
  await ensureUserProfile(data.user.id, data.user.email);
}

if (requiresPasswordSetup) {
  // If verified email requires password setup
  setMessage('Email verified successfully! Please set up a password.');
  setMessageType('success');
  setShowOtpForm(false);
  setShowPasswordSetupForm(true);
} else {
  // Regular verification flow
  setMessage('Email verified successfully!');
  setMessageType('success');
  // Process the purchase after verification
  await processPurchase(email);
}
}
} catch (error) {
setMessage(`Error during verification: ${error.message}`);
setMessageType('error');
} finally {
setLoading(false);
}
}} 
className="flex flex-col items-center w-full"
>
<motion.div
initial={{ opacity: 0, y: 10 }}
animate={{ opacity: 1, y: 0 }}
transition={{ delay: 0.1 }}
className="text-center mb-6 w-full"
>
<h2 className="text-2xl font-bold text-green mb-2">Verify Your Email</h2>
<p className="text-gray-600">Enter the 6-digit code sent to {email}</p>
</motion.div>

<VerificationBoxes 
onComplete={(code) => setOtpCode(code.join(''))}
/>

<motion.button
variants={buttonVariants}
initial="initial"
animate="animate"
whileTap="tap"
whileHover="hover"
disabled={loading || otpCode.length !== 6}
type="submit"
className="w-full py-3 px-6 mt-6 bg-green text-white rounded-full font-bold shadow-md hover:bg-accent transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
>
{loading ? (
<div className="flex items-center justify-center">
<div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
Verifying...
</div>
) : 'Confirm Code'}
</motion.button>

<motion.button
initial={{ opacity: 0 }}
animate={{ opacity: 1 }}
transition={{ delay: 0.5 }}
type="button"
onClick={() => setShowOtpForm(false)}
className="text-green text-sm mt-4 hover:text-accent transition-colors"
>
Back to email entry
</motion.button>

{renderResetButton()}
</motion.form>
);
}

// Email form (default if not showing any other form)
return (
<motion.form 
initial="hidden"
animate="visible"
exit="exit"
variants={formVariants}
onSubmit={handleVerifyEmail} 
className="space-y-4"
>
<motion.div
initial={{ opacity: 0, y: 10 }}
animate={{ opacity: 1, y: 0 }}
transition={{ delay: 0.1 }}
>
<h2 className="text-2xl font-bold text-green mb-6 text-center">Complete Your Purchase</h2>
<p className="text-gray-600 mb-6 text-center">Enter your email to continue with your purchase</p>
</motion.div>

<motion.div
initial={{ opacity: 0, x: -10 }}
animate={{ opacity: 1, x: 0 }}
transition={{ delay: 0.2 }}
className="mb-6"
>
<label 
htmlFor="email" 
className="block mb-2 font-medium text-gray-700"
>
Email:
</label>
<div className="relative">
<Mail className="absolute left-3 top-3 text-gray-400" size={18} />
<input
id="email"
type="email"
value={email}
onChange={(e) => setEmail(e.target.value)}
required
className="w-full p-3 pl-10 border-2 border-gray-200 rounded-lg focus:border-green focus:ring-0 transition-colors outline-none"
placeholder="Enter your email address"
/>
</div>
</motion.div>

<motion.button
variants={buttonVariants}
initial="initial"
animate="animate"
whileTap="tap"
whileHover="hover"
disabled={loading}
type="submit"
className="w-full py-3 px-6 bg-green text-white rounded-full font-bold shadow-md hover:bg-accent transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
>
{loading ? (
<div className="flex items-center justify-center">
<div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
Processing...
</div>
) : 'Verify Your Email'}
</motion.button>

{renderResetButton()}
</motion.form>
);
};

return (
<div className="purchase-form">
<AnimatePresence>
{message && (
<Message message={message} type={messageType} />
)}
</AnimatePresence>

<AnimatePresence mode="wait">
{renderForm()}
</AnimatePresence>

{/* Show product information if needed */}
{productId && (
<motion.div 
initial={{ opacity: 0 }}
animate={{ opacity: 1 }}
transition={{ delay: 0.6 }}
className="mt-6 p-3 bg-gray-50 rounded-lg"
>
<p className="text-sm text-gray-500 text-center">
Product ID: {productId}
</p>
</motion.div>
)}

{/* Debug information - remove in production */}
{Object.keys(debug).length > 0 && (
<motion.div 
initial={{ height: 0, opacity: 0 }}
animate={{ height: 'auto', opacity: 1 }}
transition={{ delay: 0.7 }}
className="mt-6 p-3 bg-gray-50 rounded-lg text-xs"
>
<details>
<summary className="cursor-pointer text-gray-600">Debug Info</summary>
<pre className="mt-2 overflow-auto max-h-40 p-2 bg-gray-100 rounded">
{JSON.stringify(debug, null, 2)}
</pre>
</details>
</motion.div>
)}
</div>
);
};

// Simple self-contained page with minimal styling
const SimplePurchasePage = ({ productId = '18bdfc62-c891-4690-8745-ee6d01392336' }) => {
return (
<AuthProvider>
<div className="max-w-md mx-auto my-10 p-6 bg-white rounded-2xl shadow-md">
<h2 className="text-3xl font-bold text-center text-green mb-8">Pronto estate!</h2>
<PurchaseFormContent productId={productId} />
</div>
</AuthProvider>
);
};

export default SimplePurchasePage;